<template>
  <div class="LLWysiwygOutput" :class="classList" :style="styleVars">
    <div v-if="!usingSlot" class="LLWysiwygOutput__html" v-html="html"></div>
    <slot v-else></slot>
  </div>
</template>

<script>
import sanitizeHtml from 'sanitize-html'
import emojiToHtml from '@/utils/emojiToHtml'
import mentionToHtml from '@/utils/mentionToHtml'

const allowedTags = ['strong', 'em', 'ol', 'br', 'u', 'p', 'ul', 'li', 'a']

export default {
  name: 'LLWysiwygOutput',
  props: {
    text: { type: String, default: '' },
    usingSlot: { type: Boolean, default: false },
    sanitizeFull: { type: Boolean, default: false },
    truncate: { type: Boolean, default: false },
    lines: { type: [Number, String], default: null }
  },
  computed: {
    html() {
      return !this.sanitizeFull ? this.sanitizedHtml : this.sanitizedFull
    },
    sanitizedHtml() {
      return mentionToHtml.span(emojiToHtml.span(sanitizeHtml(this.text, { allowedTags })))
    },
    sanitizedFull() {
      let currentTag
      const tagsWithSpaces = ['p', 'li', 'ul', 'ol', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'br']
      const transformTags = {}
      tagsWithSpaces.forEach((tag) => {
        transformTags[tag] = (tagName) => {
          currentTag = tagName
          return { tagName: '' }
        }
      })
      const sanitizedText = sanitizeHtml(this.text, {
        allowedTags: [],
        transformTags: {
          br: () => ({ tagName: '', text: ' ' }),
          ...transformTags
        },
        textFilter(text) {
          if (tagsWithSpaces.includes(currentTag)) {
            currentTag = undefined
            return ` ${text}`
          }
          currentTag = undefined
          return text
        }
      })
      return mentionToHtml.span(emojiToHtml.span(sanitizedText))
    },
    classList() {
      const linesNum = Number(this.lines)
      return {
        [`${this.$options.name}_truncate`]: this.truncate && (!linesNum || linesNum === 1),
        [`${this.$options.name}_clamp`]: linesNum && linesNum > 1
      }
    },
    styleVars() {
      const linesNum = Number(this.lines)
      return linesNum && linesNum > 1 ? { '--llwo-clamp': linesNum } : null
    }
  }
}
</script>

<style lang="scss" scoped>
.LLWysiwygOutput {
  $root: &;
  cursor: inherit;

  ::v-deep {
    * {
      @apply break-words;
      font-family: 'Open Sans', 'Arial', 'Noto Color Emoji', 'Noto Emoji', sans-serif;
    }
    .emoji {
      @apply w-4 inline select-all pointer-events-none;
    }
    .mention {
      @apply text-accent-01;
    }
    ul {
      list-style: disc;
      padding-left: 1.5rem;
    }
    ol {
      list-style: decimal;
      padding-left: 1.5rem;
    }
    a {
      @apply text-primary-01-300 underline;
    }
    p {
      min-height: 1em;
    }
  }

  &__html {
    #{$root}_truncate & {
      @apply truncate;
    }

    #{$root}_clamp & {
      display: -webkit-box;
      -webkit-line-clamp: var(--llwo-clamp, 2);
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }

  &.LLWysiwygOutput_clamp {
    > * {
      display: -webkit-box;
      -webkit-line-clamp: var(--llwo-clamp, 2);
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }
}
</style>
